/**
 * 🛡️ AyoCore Error Handling System
 *
 * Simple, developer-friendly error handling with clear messages and suggestions.
 * Follows the "Fail Fast, Fail Clear, Fail Helpful" philosophy.
 */

import { Value } from '@sinclair/typebox/value';
import type { TSchema } from '@sinclair/typebox';

/**
 * Calculate Levenshtein distance
 */
function levenshteinDistance(str1: string, str2: string): number {
  const matrix: number[][] = Array.from({ length: str2.length + 1 }, () =>
    Array<number>(str1.length + 1).fill(0)
  );

  for (let i = 0; i <= str1.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= str2.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= str2.length; j++) {
    for (let i = 1; i <= str1.length; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }

  return matrix[str2.length][str1.length];
}

/**
 * Calculate string similarity (simple version)
 */
function calculateSimilarity(str1: string, str2: string): number {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;

  if (longer.length === 0) return 1.0;

  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
}

/**
 * Find similar method name for typo suggestions
 */
function findSimilarMethod(input: string, availableMethods: string[]): string | null {
  const similarities = availableMethods.map(method => ({
    method,
    score: calculateSimilarity(input.toLowerCase(), method.toLowerCase()),
  }));

  similarities.sort((a, b) => b.score - a.score);

  // Return if similarity is above threshold
  return similarities[0]?.score > 0.6 ? similarities[0].method : null;
}

/**
 * Base error class for AyoCore
 */
export class AyaError extends Error {
  public readonly timestamp: string;
  public readonly errorId: string;

  constructor(
    message: string,
    public readonly context?: Record<string, unknown>,
    public readonly suggestions?: string[]
  ) {
    super(message);
    this.name = this.constructor.name;
    this.timestamp = new Date().toISOString();
    this.errorId = `AYA_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  getFormattedMessage(): string {
    let formatted = `🚨 ${this.name}: ${this.message}`;

    if (this.context && Object.keys(this.context).length > 0) {
      formatted += '\n\n📋 Context:';
      Object.entries(this.context).forEach(([key, value]) => {
        formatted += `\n  • ${key}: ${JSON.stringify(value)}`;
      });
    }

    if (this.suggestions && this.suggestions.length > 0) {
      formatted += '\n\n💡 Suggestions:';
      this.suggestions.forEach((suggestion, index) => {
        formatted += `\n  ${index + 1}. ${suggestion}`;
      });
    }

    formatted += `\n\n🔍 Error ID: ${this.errorId}`;

    return formatted;
  }
}

/**
 * Validation error with helpful suggestions
 */
export function createValidationError(
  message: string,
  field?: string,
  value?: unknown,
  schema?: string
): AyaError {
  const context: Record<string, unknown> = {};
  if (field) context.field = field;
  if (value !== undefined) context.value = value;
  if (schema) context.schema = schema;

  const suggestions = [
    'Check your input data matches the expected schema',
    'Verify all required fields are provided',
    'Ensure field types match TypeBox definitions',
  ];

  if (field) {
    suggestions.unshift(`Fix the '${field}' field`);
  }

  return new AyaError(message, context, suggestions);
}

/**
 * Database error with helpful suggestions
 */
export function createDatabaseError(message: string, operation?: string, table?: string): AyaError {
  const context: Record<string, unknown> = {};
  if (operation) context.operation = operation;
  if (table) context.table = table;

  const suggestions = [
    'Check database connection and credentials',
    'Verify table structure matches schema definition',
    'Ensure foreign key references exist',
  ];

  if (table) {
    suggestions.unshift(`Check the '${table}' table structure`);
  }

  return new AyaError(message, context, suggestions);
}

/**
 * Service error with helpful suggestions
 */
export function createServiceError(
  message: string,
  method?: string,
  repository?: string,
  availableMethods?: string[]
): AyaError {
  const context: Record<string, unknown> = {};
  if (method) context.method = method;
  if (repository) context.repository = repository;
  if (availableMethods) context.availableMethods = availableMethods;

  const suggestions = [
    'Check method name spelling and availability',
    'Verify repository is properly published',
    'Ensure network connection is stable',
  ];

  if (method && availableMethods) {
    const similar = findSimilarMethod(method, availableMethods);
    if (similar) {
      suggestions.unshift(`Did you mean '${similar}'?`);
    }
  }

  return new AyaError(message, context, suggestions);
}

/**
 * Validate data against TypeBox schema with helpful error messages
 */
export function validateWithSchema<T>(schema: TSchema, data: unknown, schemaName?: string): T {
  if (!Value.Check(schema, data)) {
    const errors = [...Value.Errors(schema, data)];
    const errorMessages = errors.map(
      error => `${error.path}: ${error.message} (got: ${JSON.stringify(error.value)})`
    );

    const message = `Schema validation failed${schemaName ? ` for ${schemaName}` : ''}:\n${errorMessages.join('\n')}`;

    throw createValidationError(message, errors[0]?.path, errors[0]?.value, schemaName);
  }

  return data as T;
}
